import React, { useState, useEffect, useMemo } from "react";
import { Button } from "../ui";
import { MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter, MDBRow, MDBCol, MDBSpinner } from "mdb-react-ui-kit";
import { api } from "../../services"; // Adjust path to your API client instance
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NativeInput from "../NativeInput";
import CustomDropdown from '../dropdown/CustomDropdown'
import DeliveryAppPriceFields from "../DeliveryAppPriceFields";
import SARIcon from "../SARIcon";
import { isPosReadOnlyEntity } from "../../utils/posReadOnly";
import {
  buildAppPricesFromEditor,
  fetchPricedAppSources,
  getAppPriceEditorState,
  getUniqueConnectedDeliveryApps,
} from "../../utils/productAppPrice";
import { getPhoneLikeNumberError } from "../../utils/validators";
import {
  resolveProductApiError,
  translateProductApiMessage,
} from "../../utils/translateProductApiMessage";

function formatPriceForDisplay(value, isFocused) {
  if (value == null || value === "") return "";
  const raw = String(value);
  if (isFocused) return raw;
  if (/^\d+$/.test(raw)) return `${raw}.00`;
  return raw;
}

export default function UpdateProductModal({
  show,
  productDetails,
  onClose,
  fetchProduct,
}) {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  // Resolve store name from product details (read-only display)
  const storeNameFromDetails =
    (productDetails && (
      productDetails.storeName ||
      (productDetails.store && productDetails.store.name) ||
      (productDetails.category_id && productDetails.category_id.storeName) ||
      (productDetails.category_id && productDetails.category_id.store && productDetails.category_id.store.name)
    )) || "";

  const storeIdFromDetails =
    (productDetails && (
      productDetails.store ||
      productDetails.storeId ||
      productDetails.store_id ||
      (productDetails.category_id && (productDetails.category_id.store || productDetails.category_id.storeId || productDetails.category_id.store_id))
    )) || "";

  const displayStoreName = storeNameFromDetails || storeIdFromDetails || "N/A";
  const [localDetails, setLocalDetails] = useState({
    nameEn: "",
    nameAr: "",
    descriptionEn: "",
    descriptionAr: "",
    price: "",
  });

const [errors, setErrors] = useState({
  nameEn: "",
  nameAr: "",
  descriptionEn: "",
  descriptionAr: "",
  price: "",
  category: "",
});

const [loading, setLoading] = useState(false);
const params = useParams();
const productId = params.id;
const [categories, setCategories] = useState([]);
const [apps, setApps] = useState([]);
const [addonApps, setAddonApps] = useState([]);
const [appPrices, setAppPrices] = useState({});
const [useDefaultAppPrices, setUseDefaultAppPrices] = useState({});
const [customPricingEnabled, setCustomPricingEnabled] = useState(true);
const [appPriceErrors, setAppPriceErrors] = useState({});
const [isDefaultPriceFocused, setIsDefaultPriceFocused] = useState(false);
const deliveryApps = useMemo(
  () => getUniqueConnectedDeliveryApps(apps, addonApps),
  [apps, addonApps]
);
const isPosProduct = isPosReadOnlyEntity(productDetails);

  const [selectedCategory, setSelectedCategory] = useState("");

  const fetchApps = async () => {
    try {
      const { apps: nextApps, addonApps: nextAddonApps } =
        await fetchPricedAppSources();
      setApps(nextApps);
      setAddonApps(nextAddonApps);
    } catch (error) {
      console.error(
        "Error fetching apps:",
        error.response?.data || error.message
      );
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      // Fetch first page with a large limit, or iterate if you want all
      let allCategories = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.get(`/categories?page=${page}&limit=100`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const cats = Array.isArray(response.data?.categories)
          ? response.data.categories
          : [];
        allCategories = allCategories.concat(cats);
        totalPages = response.data?.totalPages || 1;
        page++;
      } while (page <= totalPages);
      const filtered = allCategories.filter(
        (cat) =>
          cat?.is_foodics !== true &&
          cat?.is_tabsense !== true &&
          cat?.is_partner !== true
      );
      setCategories(filtered);
     
    } catch (error) {
      console.error(
        "Error fetching categories:",
        error.response?.data || error.message
      );
      
    }
  };

  useEffect(()=> {
    fetchCategories();
    fetchApps();
  },[])
   const handleCategorySelect = (category) => {
  setSelectedCategory(category);

  // Run validation right away
  setErrors((prev) => ({
    ...prev,
    category: validateCategory(category),
  }));
};

useEffect(() => {
  if (show && productDetails) {
    setLocalDetails({
      nameEn: productDetails.name?.en || "",
      nameAr: productDetails.name?.ar || "",
      descriptionEn: productDetails.description?.en || "",
      descriptionAr: productDetails.description?.ar || "",
      price:
        productDetails.price !== undefined && productDetails.price !== null
          ? productDetails.price.toString()
          : "",
    });
    const editor = getAppPriceEditorState(productDetails, deliveryApps);
    setAppPrices(editor.values);
    setUseDefaultAppPrices(editor.useDefault);
    setCustomPricingEnabled(true);
    setAppPriceErrors({});
    setSelectedCategory(productDetails?.category_id);
    setErrors({
      nameEn: "",
      nameAr: "",
      descriptionEn: "",
      descriptionAr: "",
      price: "",
      category: "",
    });
  }
}, [show, productDetails, deliveryApps]);

const validateCategory = (value) => {
  if (!value || !value._id) return `${t("Category is required")}`;
  return "";
};
const validateName = (name, value) => {
  if (!value.trim()) return `${t("Name is required")}`;

  const phoneError = getPhoneLikeNumberError(value, t);
  if (phoneError) return phoneError;

  if (name === "nameEn") {
    const hasArabic = /[\u0600-\u06FF]/.test(value);
    if (hasArabic) return `${t("English name must only contain English letters and numbers.")}`;
  }

  if (name === "nameAr") {
    const hasLatin = /[A-Za-z]/.test(value);
    if (hasLatin) return `${t("Arabic name must only contain Arabic letters and numbers.")}`;
  }

  if (value.trim().length > 100)
    return `${t("Name must be less than 100 characters")}`;

  return "";
};

  const validateDescription = (name, value) => {
  if (!value.trim()) return `${t("Description is required")}`;

  const phoneError = getPhoneLikeNumberError(value, t);
  if (phoneError) return phoneError;

  if (name === "descriptionEn") {
    const hasArabic = /[\u0600-\u06FF]/.test(value);
    if (hasArabic) return `${t("English description must only contain English letters and numbers.")}`;
  }

  if (name === "descriptionAr") {
    const hasLatin = /[A-Za-z]/.test(value);
    if (hasLatin) return `${t("Arabic description must only contain Arabic letters and numbers.")}`;
  }

  if (value.trim().length > 500)
    return `${t("Description must be less than 500 characters")}`;
    
  return "";
};

  const validatePrice = (value) => {
    if (value === "" || value === null) return `${t("Price is required")}`;
    const numberValue = parseFloat(value);
    if (numberValue <= 0) return `${t("Price must be greater than zero")}`;
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "price") {
      let cleanedValue = value.replace(/[^0-9.]/g, "");
      const decimalCount = (cleanedValue.match(/\./g) || []).length;
      if (decimalCount > 1) return;

      const parts = cleanedValue.split(".");
      if (parts[1] && parts[1].length > 2) return;

      if (
        cleanedValue.length > 1 &&
        cleanedValue[0] === "0" &&
        cleanedValue[1] !== "."
      ) {
        return;
      }

      setLocalDetails((prev) => ({
        ...prev,
        [name]: cleanedValue,
      }));

      setErrors((prev) => ({
        ...prev,
        price: validatePrice(cleanedValue),
      }));
    } else {
      setLocalDetails((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "nameEn" || name === "nameAr") {
        setErrors((prev) => ({
          ...prev,
          [name]: validateName(name, value),
        }));
      } else if (name === "descriptionEn" || name === "descriptionAr") {
        setErrors((prev) => ({
          ...prev,
          [name]: validateDescription(name, value),
        }));
      }
    }
  };

  const handlePricePaste = (e) => {
    e.preventDefault();
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    const cleanedPaste = paste.replace(/[^0-9.]/g, "");
    if (cleanedPaste && /^\d+(\.\d{0,2})?$/.test(cleanedPaste)) {
      handleChange({ target: { name: "price", value: cleanedPaste } });
    }
  };

  const handlePriceKeyPress = (e) => {
    const char = e.key;

    if (e.metaKey || e.ctrlKey) return;
    // Allow control keys
    if (
    [
      "Backspace",
      "Tab",
      "Escape",
      "Enter",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ].includes(char)
  ) {
    return;
  }

    // Allow numbers
    if (/[0-9]/.test(char)) return;

    // Allow decimal point only if not already present
    if (char === "." && !e.target.value.includes(".")) return;

    e.preventDefault(); // block all other characters
  };

const validateAll = () => {
  const newErrors = {
  
    nameEn: validateName("nameEn", localDetails.nameEn),
    nameAr: validateName("nameAr", localDetails.nameAr),
    descriptionEn: validateDescription("descriptionEn", localDetails.descriptionEn),
    descriptionAr: validateDescription("descriptionAr", localDetails.descriptionAr),
    price: validatePrice(localDetails.price),
    category: validateCategory(selectedCategory),
    
  };

    const nextAppPriceErrors = {};
    if (!isPosProduct && customPricingEnabled) {
      deliveryApps.forEach((app) => {
        if (useDefaultAppPrices[app.key] !== false) return;
        const message = validatePrice(appPrices[app.key]);
        if (message) nextAppPriceErrors[app.key] = message;
      });
    }
    setAppPriceErrors(nextAppPriceErrors);

    setErrors(newErrors);
    return (
      Object.values(newErrors).every((err) => !err) &&
      Object.values(nextAppPriceErrors).every((err) => !err)
    );
  };

  const handleSave = async () => {
    if (!validateAll()) {
      // Frontend validation: show only inline errors, no toast
      return;
    }

  setLoading(true);
  try {
    const token = localStorage.getItem("token");
    const payload = {
      name: {
        en: localDetails.nameEn.trim(),
        ar: localDetails.nameAr.trim(),
      },
      description: {
        en: localDetails.descriptionEn.trim(),
        ar: localDetails.descriptionAr.trim(),
      },
       categoryId: selectedCategory._id,
      price: parseFloat(localDetails.price),
      ...(isPosProduct
        ? {}
        : {
            app_prices: buildAppPricesFromEditor({
              deliveryApps,
              values: appPrices,
              useDefault: useDefaultAppPrices,
              customPricingEnabled,
            }),
          }),
    };

      const response = await api.patch(`/product/${productId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        toast.success(
          translateProductApiMessage(
            response.data.message,
            t
          ) || t("productApiMsgs.updatedSuccessfully")
        );
        onClose();
        if (typeof fetchProduct === "function") await fetchProduct();
      }
    } catch (error) {
      toast.error(
        resolveProductApiError(error, t, "productApiMsgs.failedToUpdate")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) onClose();
  };

  return (
    <MDBModal open={show} onClose={handleClose} tabIndex="-1" className="oh-modal oh-modal--update-product">
      <MDBModalDialog size="md" centered>
        <MDBModalContent>
         <MDBModalHeader>
    <MDBModalTitle>{t("Update Product")}</MDBModalTitle>
  <button
    type="button"
    className="btn-close"
    onClick={handleClose}
    disabled={loading}
    aria-label="Close"
  />
</MDBModalHeader>

          <MDBModalBody>
             <MDBRow className="mb-3">
               <MDBCol>
                 <NativeInput
                   label={t("store")}
                  
                   value={displayStoreName}
                 
                   disabled={true}
                
                 />
               
               </MDBCol>
             </MDBRow>
              <MDBRow className="mb-3">
               <MDBCol><CustomDropdown
                            options={categories}
                            showLable={true}
                            label={t("Store Category")}
                            selectedOption={selectedCategory}
                            handleSelect={handleCategorySelect}
                            placeholder={t("Select a category")}
                            backgroundColor="#fff"
                            invalid={!!errors.category}
                          />
                           {errors.category && (
      <div className="text-danger mt-1 small">
        {errors.category}
      </div>
    )}
                          </MDBCol>
                          </MDBRow>
                          
            <MDBRow className="mb-3">
    
              <MDBCol>
                <NativeInput
                  dir={i18n.dir()}
                  label={t("nameen")}
                  id="nameEn"
                  name="nameEn"
                  value={localDetails.nameEn}
                  onChange={handleChange}
                  disabled={loading}
                  invalid={!!errors.nameEn}
                  feedback={errors.nameEn || ""}
                  className={i18n.dir() === "rtl" ? "text-end" : ""}
                />
              </MDBCol>
            </MDBRow>
          
            <MDBRow className="mb-3">
              <MDBCol>
                <NativeInput
                  label={t("namear")}
                  id="nameAr"
                  name="nameAr"
                  value={localDetails.nameAr}
                  onChange={handleChange}
                  disabled={loading}
                  invalid={!!errors.nameAr}
                  feedback={errors.nameAr || ""}
                />
              </MDBCol>
            </MDBRow>

            <MDBRow className="mb-3">
              <MDBCol>
                <NativeInput
                  label={t("descriptionen")}
                  id="descriptionEn"
                  name="descriptionEn"
                  value={localDetails.descriptionEn}
                  onChange={handleChange}
                  disabled={loading}
                  invalid={!!errors.descriptionEn}
                  feedback={errors.descriptionEn || ""}
                />
              </MDBCol>
            </MDBRow>

            <MDBRow className="mb-3">
              <MDBCol>
                <NativeInput
                  label={t("descriptionar")}
                  id="descriptionAr"
                  name="descriptionAr"
                  value={localDetails.descriptionAr}
                  onChange={handleChange}
                  disabled={loading}
                  invalid={!!errors.descriptionAr}
                  feedback={errors.descriptionAr || ""}
                />
              </MDBCol>
            </MDBRow>

            <MDBRow className="mb-3">
              <MDBCol>
                <div
                  className={[
                    "oh-field",
                    "oh-field--price",
                    errors.price ? "oh-field--invalid" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <label htmlFor="price" className="oh-field__label form-label">
                    {t("defaultPrice")}
                  </label>
                  <div className="oh-field__price-wrap" dir="ltr">
                    <span className="oh-field__price-prefix" aria-hidden="true">
                      <SARIcon size={14} color="#030f1b" />
                    </span>
                    <input
                      id="price"
                      name="price"
                      type="text"
                      inputMode="decimal"
                      className="oh-field__control oh-field__control--tabular oh-field__control--price"
                      value={formatPriceForDisplay(
                        localDetails.price,
                        isDefaultPriceFocused
                      )}
                      onChange={handleChange}
                      onKeyDown={handlePriceKeyPress}
                      onPaste={handlePricePaste}
                      onFocus={() => setIsDefaultPriceFocused(true)}
                      onBlur={() => setIsDefaultPriceFocused(false)}
                      disabled={loading}
                      aria-invalid={errors.price ? "true" : "false"}
                    />
                  </div>
                  <div
                    className="oh-field__error invalid-feedback"
                    role={errors.price ? "alert" : undefined}
                  >
                    {errors.price || ""}
                  </div>
                </div>
              </MDBCol>
            </MDBRow>

            {!isPosProduct && deliveryApps.length > 0 ? (
              <MDBRow className="mb-3">
                <MDBCol>
                  <DeliveryAppPriceFields
                    layout="stack"
                    deliveryApps={deliveryApps}
                    values={appPrices}
                    useDefault={useDefaultAppPrices}
                    customPricingEnabled={customPricingEnabled}
                    defaultPrice={localDetails.price}
                    errors={appPriceErrors}
                    disabled={loading}
                    onChange={(key, value) => {
                      setAppPrices((prev) => ({ ...prev, [key]: value }));
                      setAppPriceErrors((prev) => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                      });
                    }}
                    onToggleUseDefault={(key, checked) => {
                      setUseDefaultAppPrices((prev) => ({
                        ...prev,
                        [key]: checked,
                      }));
                      setAppPriceErrors((prev) => {
                        const next = { ...prev };
                        delete next[key];
                        return next;
                      });
                    }}
                    onToggleCustomPricing={(enabled) => {
                      setCustomPricingEnabled(enabled);
                      if (enabled) {
                        setUseDefaultAppPrices((prev) => {
                          const next = { ...prev };
                          deliveryApps.forEach((app) => {
                            if (next[app.key] === undefined) next[app.key] = true;
                          });
                          return next;
                        });
                      } else {
                        setAppPriceErrors({});
                      }
                    }}
                  />
                </MDBCol>
              </MDBRow>
            ) : null}

          </MDBModalBody>

          {isArabic ? (
            <MDBModalFooter>
              <Button color="primary" size="sm" className="oh-modal__footer-btn" onClick={handleSave} disabled={loading}>
                {loading && <MDBSpinner size="sm" className="me-2" />}
                {t("save")}
              </Button>
              <Button
                color="secondary"
                size="sm"
                className="oh-modal__footer-btn"
                onClick={handleClose}
                disabled={loading}
              >
                {t("cancel")}
              </Button>
            </MDBModalFooter>
          ) : (
            <MDBModalFooter>
              <Button
                color="secondary"
                size="sm"
                className="oh-modal__footer-btn"
                onClick={handleClose}
                disabled={loading}
              >
                {t("cancel")}
              </Button>
              <Button color="primary" size="sm" className="oh-modal__footer-btn" onClick={handleSave} disabled={loading}>
                {loading && <MDBSpinner size="sm" className="me-2" />}
                {t("save")}
              </Button>
            </MDBModalFooter>
          )}
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}
